import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AssistantState, Message, Session, MessageRole } from "./types";
import type { RootState } from "app/store";

const initialState: AssistantState = {
  sessions: [],
  currentSessionId: null,
  messages: [],
  isLoading: false,
  isLoadingMessages: false,
  isTyping: false,
  error: null,
};

const assistantSlice = createSlice({
  name: "assistant",
  initialState,
  reducers: {
    setSessions: (state, action: PayloadAction<Session[]>) => {
      state.sessions = action.payload;
    },
    setCurrentSession: (state, action: PayloadAction<string | null>) => {
      state.currentSessionId = action.payload;
    },
    addSession: (state, action: PayloadAction<Session>) => {
      state.sessions.unshift(action.payload);
      state.currentSessionId = action.payload.id;
    },
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload;
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
    },
    updateLastMessage: (state, action: PayloadAction<string>) => {
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.role === MessageRole.ASSISTANT) {
        lastMessage.content += action.payload;
      }
    },
    removeLastEmptyAssistantMessage: (state) => {
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.role === MessageRole.ASSISTANT && !lastMessage.content) {
        state.messages.pop();
      }
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsLoadingMessages: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMessages = action.payload;
    },
    setIsTyping: (state, action: PayloadAction<boolean>) => {
      state.isTyping = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearChat: (state) => {
      state.currentSessionId = null;
      state.messages = [];
      state.isTyping = false;
      state.isLoadingMessages = false;
      state.error = null;
    },
    resetState: () => initialState,
  },
});

export const actions = { ...assistantSlice.actions };

export const selectSessions = (state: RootState) => state.assistant.sessions;
export const selectCurrentSessionId = (state: RootState) => state.assistant.currentSessionId;
export const selectMessages = (state: RootState) => state.assistant.messages;
export const selectIsLoading = (state: RootState) => state.assistant.isLoading;
export const selectIsLoadingMessages = (state: RootState) => state.assistant.isLoadingMessages;
export const selectIsTyping = (state: RootState) => state.assistant.isTyping;
export const selectError = (state: RootState) => state.assistant.error;

export default assistantSlice.reducer;
